import { defineEvent } from "@amtana/module";
import { z } from "zod";

import { planIdSchema } from "./billing.schemas.ts";
import { paymentProviderSchema } from "./payment-providers.schemas.ts";

// The facts of one organization's billing (ADR-022 points 1-2, epic 26, story S26.04): one
// stream per organization, `billing:<organizationId>` (matching how `BillingOrganizationRecord`
// already keys the row these facts describe), appended inside the same transaction as the row
// that describes them, so a crash or a lost Stripe answer never leaves a row without its fact
// or a fact without its row. Nothing reacts to these yet (S26.05 is the router); they live in
// the contract so a future peer can.

export const BILLING = "billing";

/** A checkout completing, a renewal invoice Stripe confirms paid, or a credit pack landed
 * through any provider — Stripe's checkout or a paid Swiss Bitcoin Pay invoice (ADR-018 §3)
 * are payments too (Decided in spec, S26.04). `payerUserId` is the person whose checkout it
 * was, null when Stripe charged a subscription on its own schedule; `provider` is which one
 * took the payment. `kind` and `description` (S26.11, D512) say what was bought, so amtana's
 * own receipt can name it: a renewal is always `"plan"`. `adminUserIds` (D490's own shape) is
 * every admin of the organization at the moment it charged, read only when `payerUserId` is
 * null, so the receipt still has somewhere to go on a renewal Stripe charged on its own
 * schedule. */
export const paymentSucceeded = defineEvent(
  BILLING,
  "payment_succeeded",
  z.object({
    organizationId: z.string(),
    plan: planIdSchema,
    kind: z.enum(["plan", "credits"]),
    description: z.string(),
    amountCents: z.number().int().nonnegative(),
    currency: z.string().length(3),
    invoiceId: z.string(),
    payerUserId: z.string().nullable(),
    adminUserIds: z.array(z.string()),
    provider: paymentProviderSchema,
  }),
);

/** An invoice Stripe could not charge; `graceEndsAt` is when the organization drops to
 * past-due's grace ending (`effectiveStatus`). `adminUserIds` is every admin of the
 * organization at the moment it failed (ADR-022 point 3, S26.14): the router reads who hears
 * this from the fact itself, since it cannot peer back to organization for a live read
 * (notification would cycle with organization's own peer on it). */
export const paymentFailed = defineEvent(
  BILLING,
  "payment_failed",
  z.object({
    organizationId: z.string(),
    graceEndsAt: z.iso.datetime(),
    adminUserIds: z.array(z.string()),
  }),
);

/** A spend that took the shared balance from above the low-credit line to at or below it
 * (`crossedIntoLowCredits`); fires once per crossing. `adminUserIds`: see `paymentFailed`. */
export const creditsLow = defineEvent(
  BILLING,
  "credits_low",
  z.object({
    organizationId: z.string(),
    remainingCredits: z.number().nullable(),
    allowanceCredits: z.number().nullable(),
    adminUserIds: z.array(z.string()),
  }),
);

/** A usage milestone reached this period (`dueUsageWarnings`), as the fraction of the
 * allowance used: 0.8, 1, and so on. `adminUserIds`: see `paymentFailed`. */
export const usageThresholdCrossed = defineEvent(
  BILLING,
  "usage_threshold_crossed",
  z.object({
    organizationId: z.string(),
    threshold: z.number(),
    adminUserIds: z.array(z.string()),
  }),
);

/** An auto-recharge off-session charge the payment provider refused. `adminUserIds`: see
 * `paymentFailed`. */
export const rechargeFailed = defineEvent(
  BILLING,
  "recharge_failed",
  z.object({
    organizationId: z.string(),
    packCents: z.number().int().nonnegative(),
    reason: z.string().nullable(),
    adminUserIds: z.array(z.string()),
  }),
);

/** A manual subscription's own end date passed, dropping the organization to Free
 * (the scheduled sweep, `expireDueManualSubscriptions`). `adminUserIds`: see `paymentFailed`. */
export const manualSubscriptionEnded = defineEvent(
  BILLING,
  "manual_subscription_ended",
  z.object({
    organizationId: z.string(),
    adminUserIds: z.array(z.string()),
  }),
);

export const billingEvents = [
  paymentSucceeded,
  paymentFailed,
  creditsLow,
  usageThresholdCrossed,
  rechargeFailed,
  manualSubscriptionEnded,
] as const;
